import { IEditorV3 } from "@azu-business/editor-v3";
import { ARS, ArsTableBody, ArsTableGroupCalculationDto, totalSidebarItem } from "aic";
import { HierarchyReadDto } from "../hierarchy/HierarchyReadDto";
import { TlfItemReadDto } from "./TlfItemReadDto";

interface ArsItems {
  analysisMethods: ARS.AnalysisMethod[];
  operations: ARS.Operation[];
  analysisSets: ARS.AnalysisSet[];
  dataSubsets: ARS.DataSubset[];
  groupingFactors: ARS.GroupingFactor[];
}

/**
 * Coalesces ARS items from multiple hierarchies into a single collection.
 *
 * @param hierarchies - Array of hierarchy objects containing ARS items
 * @returns Coalesced ARS items from all provided hierarchies
 */
const coalesceArsItemsFromHierarchies = (hierarchies: HierarchyReadDto[]): ArsItems => {
  const arsItems: ArsItems = {
    analysisMethods: [
      ...hierarchies
        .flatMap((h) => h.analysisMethods)
        .filter((am): am is ARS.AnalysisMethod => am !== undefined),
    ],
    operations: [],
    analysisSets: [
      ...hierarchies
        .flatMap((h) => h.analysisSets)
        .filter((as): as is ARS.AnalysisSet => as !== undefined),
    ],
    dataSubsets: [
      ...hierarchies
        .flatMap((h) => h.dataSubsets)
        .filter((ds): ds is ARS.DataSubset => ds !== undefined),
    ],
    groupingFactors: [
      ...hierarchies
        .flatMap((h) => h.groupingFactors)
        .filter((gf): gf is ARS.GroupingFactor => gf !== undefined),
    ],
  };
  arsItems.operations = arsItems.analysisMethods.flatMap((am) => am.operations);
  return arsItems;
};

/**
 * Hydrates a single table group with the corresponding ARS items based on its identifiers.
 *
 * @param group - The table group to hydrate
 * @param arsItems - Available ARS items for hydration
 */
const hydrateArsTableGroup = (
  group: ArsTableGroupCalculationDto<IEditorV3>,
  arsItems: ArsItems,
) => {
  // Implement the hydration logic for the ArsTableGroup here
  if ("analysisMethodId" in group) {
    group.analysisMethod = arsItems.analysisMethods.find((am) => am.id === group.analysisMethodId)!;
  } else if ("operationId" in group) {
    group.operation = arsItems.operations.find((op) => op.id === group.operationId)!;
  } else if ("groupingFactorId" in group) {
    group.groupingFactor = arsItems.groupingFactors.find(
      (gf) => gf.id === group.groupingFactorId,
    ) ??
      // If the grouping factor is not found, then add a placeholder item
      { ...totalSidebarItem.data().dropItem, id: group.groupingFactorId };
  }
  group.subGroups?.forEach((sg) => hydrateArsTableGroup(sg, arsItems));
};

/**
 * Hydrates the provided TLF item with ARS items from the given hierarchy loader data.
 *
 * @param hierarchies - The hierarchies containing ARS items for hydration
 * @param tlfItem - The TLF item to be hydrated with ARS items
 */
export const hydrateArsTableBody = (hierarchies: HierarchyReadDto[], tlfItem: TlfItemReadDto) => {
  // Only ARS tables need this
  if (!("columns" in tlfItem.body && "rows" in tlfItem.body)) return;
  // Implement the hydration logic for ARS items into the ArsTable here
  const body = tlfItem.body as ArsTableBody<IEditorV3>;
  const arsItems = coalesceArsItemsFromHierarchies(hierarchies);

  body.columns.forEach((g) => hydrateArsTableGroup(g, arsItems));
  body.rows.forEach((g) => hydrateArsTableGroup(g, arsItems));
};
